// client/src/pages/Admin/UserManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Switch,
  Grid,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { User, UserRole, CreateUserDTO, UpdateUserDTO } from '../../types/user.types';
import { createUser, updateUser, deleteUser, getAllUsers } from '../../api/users';
import * as lookupApi from '../../api/lookups';
import { Lookup } from '../../types/lookup.types';
import api from '../../utils/api';
import { useSnackbar } from 'notistack';

const USER_ROLES: UserRole[] = ['administrator', 'sales', 'user', 'lead_gen'];

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  subBrokerLevel?: string;
}

const initialFormData: FormData = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'user',
  subBrokerLevel: ''
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const { enqueueSnackbar } = useSnackbar();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [subBrokerLevels, setSubBrokerLevels] = useState<Lookup[]>([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedUsers = await getAllUsers();
      setUsers(Array.isArray(fetchedUsers) ? fetchedUsers : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setUsers([]); // Ensure users is an empty array on error
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
    fetchSubBrokerLevels();
  }, [fetchUsers]);

  const fetchSubBrokerLevels = async () => {
    try {
      const levels = await lookupApi.getLookupsByType('SubBrokerLevel');
      setSubBrokerLevels(levels);
    } catch (err) {
      console.error('Failed to fetch sub-broker levels:', err);
    }
  };

  const formatDate = (date: string | Date | undefined | null) => {
    if (!date) return 'Never';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<UserRole>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value as UserRole,
      // Clear subBrokerLevel if role is not 'user'
      subBrokerLevel: value === 'user' ? prev.subBrokerLevel : ''
    }));
  };

  const handleSubBrokerLevelChange = (e: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      subBrokerLevel: e.target.value
    }));
  };

  const validateForm = () => {
    if (!formData.email) return 'Email is required';
    if (!formData.firstName) return 'First Name is required';
    if (!formData.lastName) return 'Last Name is required';
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const userData = {
        ...formData
      };

      if (editingUser) {
        // Update existing user
        const updatedUser = await updateUser(editingUser._id, userData as UpdateUserDTO);
        setUsers(prev => prev.map(u => u._id === updatedUser._id ? updatedUser : u));
        enqueueSnackbar('User updated successfully', { variant: 'success' });
      } else {
        // Create new user
        const newUser = await createUser(userData as CreateUserDTO);
        setUsers(prev => [...prev, newUser]);
        enqueueSnackbar('User created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Operation failed';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) return;

    setLoading(true);
    setError(null);
    try {
      await deleteUser(user._id);
      enqueueSnackbar('User deleted successfully', { variant: 'success' });
      fetchUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordClick = (user: User) => {
    setUserToReset(user);
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setUserToReset(null);
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;
    
    setResetLoading(true);
    try {
      // Use the same endpoint as the public ForgotPassword page
      await api.post('/auth/forgot-password', { email: userToReset.email });
      enqueueSnackbar('Password reset email sent successfully.', { variant: 'success' });
      handleCloseResetDialog();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset email';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await updateUser(user._id, { isActive: !user.isActive });
      enqueueSnackbar(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`, { variant: 'success' });
      fetchUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role,
        subBrokerLevel: user.subBrokerLevel || ''
      });
    } else {
      setEditingUser(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData(initialFormData);
    setError(null);
  };

  const capitalizeRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.9)',
              },
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer 
        component={Paper} 
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: '0 4px'
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ 
              '& th': { 
                color: '#94a3b8', 
                fontWeight: 600,
                borderBottom: '2px solid rgba(59, 130, 246, 0.3)',
                backgroundColor: 'rgba(15, 23, 42, 0.6)',
                padding: '16px 8px',
                fontSize: '0.875rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              } 
            }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Account Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user._id} 
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': { 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                  },
                  '& .MuiTableCell-root': { 
                    color: 'white',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <TableCell>
                  {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={capitalizeRole(user.role)}
                    color={
                      user.role === 'administrator'
                        ? 'error'
                        : user.role === 'sales'
                        ? 'primary'
                        : 'default'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {formatDate(user.createdAt)}
                </TableCell>
                <TableCell>
                  {formatDate(user.lastLogin)}
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography
                      variant="body2"
                      color={user.isActive ? 'success.main' : 'error.main'}
                      sx={{ mr: 1 }}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                    <Switch
                      checked={user.isActive}
                      onChange={() => handleToggleStatus(user)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase': {
                          color: '#94a3b8',
                          '&.Mui-checked': {
                            color: '#3b82f6',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'rgba(59, 130, 246, 0.5)',
                              opacity: 1
                            },
                          },
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(148, 163, 184, 0.3)',
                          opacity: 1
                        }
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    onClick={() => handleOpenDialog(user)} 
                    size="small"
                    sx={{
                      color: '#60a5fa',
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      margin: '0 4px',
                      '&:hover': {
                        backgroundColor: 'rgba(96, 165, 250, 0.2)',
                      }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleResetPasswordClick(user)} 
                    size="small" 
                    sx={{
                      color: '#10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      margin: '0 4px',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                      }
                    }}
                  >
                    <LockResetIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(user)} 
                    size="small" 
                    sx={{
                      color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      margin: '0 4px',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Edit/Create Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            color: 'white',
          }
        }}
      >
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={handleSelectChange}
                    name="role"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                      },
                      color: 'white',
                      '& .MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(15, 23, 42, 0.9)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
                        }
                      }
                    }}
                  >
                    {USER_ROLES.map((role) => (
                      <MenuItem 
                        key={role} 
                        value={role}
                        sx={{
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(59, 130, 246, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(59, 130, 246, 0.4)'
                            }
                          }
                        }}
                      >
                        {capitalizeRole(role).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                      },
                      color: 'white',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                      },
                      color: 'white',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                      },
                      color: 'white',
                    }
                  }}
                />
              </Grid>

              {/* Sub-Broker Level - Only show for 'user' role */}
              {formData.role === 'user' && (
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Sub-Broker Level</InputLabel>
                    <Select
                      value={formData.subBrokerLevel || ''}
                      onChange={handleSubBrokerLevelChange}
                      label="Sub-Broker Level"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#60a5fa',
                        },
                        color: 'white',
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.9)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
                          }
                        }
                      }}
                    >
                      <MenuItem 
                        value=""
                        sx={{
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        <em>None</em>
                      </MenuItem>
                      {subBrokerLevels.map((level) => (
                        <MenuItem 
                          key={level._id} 
                          value={level.value}
                          sx={{
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(59, 130, 246, 0.3)',
                              '&:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.4)'
                              }
                            }
                          }}
                        >
                          {level.value} - {level.metadata?.commissionRate || 0}%
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      Optional: Assign a sub-broker commission level to this user
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{
              backgroundColor: '#3b82f6',
              '&:hover': {
                backgroundColor: '#2563eb',
              }
            }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Confirmation Dialog */}
      <Dialog 
        open={resetDialogOpen} 
        onClose={handleCloseResetDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            color: 'white',
          }
        }}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body1">
              Are you sure you want to send a password reset email to {userToReset?.email}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will send an email with a password reset link that will allow the user to set a new password.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseResetDialog}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained" 
            disabled={resetLoading}
            sx={{
              backgroundColor: resetLoading ? 'rgba(59, 130, 246, 0.5)' : '#3b82f6',
              '&:hover': {
                backgroundColor: resetLoading ? 'rgba(59, 130, 246, 0.5)' : '#2563eb',
              },
              color: resetLoading ? 'rgba(255, 255, 255, 0.7)' : 'white'
            }}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
